import { Routes, Route } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { AuthProvider } from './context/AuthContext'
import { AdminProvider } from './context/AdminContext'
import Layout from './components/layout/Layout'
import PortalLayout from './components/layout/PortalLayout'
import AdminLayout from './components/layout/AdminLayout'
import ProtectedRoute from './components/auth/ProtectedRoute'
import ProtectedAdminRoute from './components/auth/ProtectedAdminRoute'
import LoadingSpinner from './components/common/LoadingSpinner'

// Lazy load pages for code splitting
const HomePage = lazy(() => import('./pages/public/HomePage'))
const AboutPage = lazy(() => import('./pages/public/AboutPage'))
const ServicesPage = lazy(() => import('./pages/public/ServicesPage'))
const ServiceDetailPage = lazy(() => import('./pages/public/ServiceDetailPage'))
const IndustriesPage = lazy(() => import('./pages/public/IndustriesPage'))
const IndustryDetailPage = lazy(() => import('./pages/public/IndustryDetailPage'))
const ContactPage = lazy(() => import('./pages/public/ContactPage'))
const BlogPage = lazy(() => import('./pages/public/BlogPage'))
const FAQPage = lazy(() => import('./pages/public/FAQPage'))
const TeamPage = lazy(() => import('./pages/public/TeamPage'))
const PrivacyPolicyPage = lazy(() => import('./pages/public/PrivacyPolicyPage'))
const TermsPage = lazy(() => import('./pages/public/TermsPage'))
const ToolsPage = lazy(() => import('./pages/public/ToolsPage'))
const CalculatorPage = lazy(() => import('./pages/public/CalculatorPage'))
const NotFoundPage = lazy(() => import('./pages/public/NotFoundPage'))

// Auth pages
const LoginPage = lazy(() => import('./pages/auth/LoginPage'))
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'))

// Portal pages
const PortalDashboard = lazy(() => import('./pages/portal/DashboardPage'))
const PortalDocuments = lazy(() => import('./pages/portal/DocumentsPage'))
const PortalUpload = lazy(() => import('./pages/portal/UploadPage'))
const PortalProfile = lazy(() => import('./pages/portal/ProfilePage'))

// Admin pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboardPage'))
const AdminClients = lazy(() => import('./pages/admin/AdminClientsPage'))
const AdminClientDetail = lazy(() => import('./pages/admin/AdminClientDetailPage'))
const AdminDocuments = lazy(() => import('./pages/admin/AdminDocumentsPage'))
const AdminContacts = lazy(() => import('./pages/admin/AdminContactsPage'))
const AdminCreateClient = lazy(() => import('./pages/admin/AdminCreateClientPage'))

function App() {
  return (
    <AuthProvider>
      <AdminProvider>
        <Suspense fallback={<LoadingSpinner fullScreen />}>
          <Routes>
            {/* Public routes with main layout */}
            <Route path="/" element={<Layout />}>
              <Route index element={<HomePage />} />
              <Route path="about" element={<AboutPage />} />
              <Route path="services" element={<ServicesPage />} />
              <Route path="services/:slug" element={<ServiceDetailPage />} />
              <Route path="industries" element={<IndustriesPage />} />
              <Route path="industries/:slug" element={<IndustryDetailPage />} />
              <Route path="contact" element={<ContactPage />} />
              <Route path="blog" element={<BlogPage />} />
              <Route path="faq" element={<FAQPage />} />
              <Route path="team" element={<TeamPage />} />
              <Route path="privacy" element={<PrivacyPolicyPage />} />
              <Route path="terms" element={<TermsPage />} />
              <Route path="tools" element={<ToolsPage />} />
              <Route path="tools/:slug" element={<CalculatorPage />} />
            </Route>

            {/* Auth routes */}
            <Route path="login" element={<LoginPage />} />
            <Route path="forgot-password" element={<ForgotPasswordPage />} />

            {/* Protected Portal routes */}
            <Route
              path="portal"
              element={
                <ProtectedRoute>
                  <PortalLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<PortalDashboard />} />
              <Route path="documents" element={<PortalDocuments />} />
              <Route path="upload" element={<PortalUpload />} />
              <Route path="profile" element={<PortalProfile />} />
            </Route>

            {/* Protected Admin routes */}
            <Route
              path="admin"
              element={
                <ProtectedAdminRoute>
                  <AdminLayout />
                </ProtectedAdminRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="clients" element={<AdminClients />} />
              <Route path="clients/:clientId" element={<AdminClientDetail />} />
              <Route path="documents" element={<AdminDocuments />} />
              <Route path="contacts" element={<AdminContacts />} />
              <Route path="create-client" element={<AdminCreateClient />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </AdminProvider>
    </AuthProvider>
  )
}

export default App
